import { useMutation, useQuery } from '@tanstack/react-query';
import {
  finishCourseExercise,
  finishExercise,
  getContentViewHistory,
  getContentViewStatus,
  IContentViewHistoryParams,
  setContentView,
} from '../queries/contentViews';
import { ViewContentType, ViewOrigin } from '../models/contentView';

/**
 * Finaliza um exercício sem resolver (web, por slug). Unidirecional: registra
 * resposta errada e marca como visto. Para reverter, use `useSetContentView`.
 */
export function useFinishExercise() {
  const { mutate, mutateAsync, isPending, isSuccess, data } = useMutation({
    mutationKey: ['finish-exercise'],
    mutationFn: ({ slug, origin }: { slug: string; origin?: ViewOrigin }) =>
      finishExercise(slug, origin),
    retry: false,
  });

  return { mutate, mutateAsync, isLoading: isPending, isSuccess, data };
}

/** Finaliza um exercício sem resolver dentro de um conteúdo de curso (mobile). */
export function useFinishCourseExercise() {
  const { mutate, mutateAsync, isPending, isSuccess, data } = useMutation({
    mutationKey: ['finish-course-exercise'],
    mutationFn: ({
      courseId,
      contentId,
      exerciseId,
      origin,
    }: {
      courseId: string;
      contentId: string;
      exerciseId: string;
      origin?: ViewOrigin;
    }) => finishCourseExercise(courseId, contentId, exerciseId, origin),
    retry: false,
  });

  return { mutate, mutateAsync, isLoading: isPending, isSuccess, data };
}

/** Marca/desmarca explicitamente um conteúdo (não é toggle). */
export function useSetContentView() {
  const { mutate, mutateAsync, isPending, isSuccess, data } = useMutation({
    mutationKey: ['set-content-view'],
    mutationFn: setContentView,
    retry: false,
  });

  return { mutate, mutateAsync, isLoading: isPending, isSuccess, data };
}

/** Consulta o status atual de visualização de um conteúdo. */
export function useContentViewStatus(
  contentType: ViewContentType,
  contentId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['content-view-status', contentType, contentId],
    queryFn: () => getContentViewStatus(contentType, contentId as string),
    enabled: enabled && !!contentId,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Histórico paginado de visualizações do usuário. */
export function useContentViewHistory(params: IContentViewHistoryParams = {}) {
  return useQuery({
    queryKey: ['content-view-history', params],
    queryFn: () => getContentViewHistory(params),
    retry: false,
    refetchOnWindowFocus: false,
  });
}
